import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Box, Typography } from '@mui/material';
import { ColorizeOutlinedIcon, LockOutlinedIcon } from 'icons/materialIcons';
import { ChromePicker } from 'react-color';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import { useParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const TeamQRCodeColorPicker = ({ isColorRestricted = false, canRequestChange = false, onRestrictionNotification, cardId: propCardId }) => {
    const colorOptions = ['#000000', '#eb5757', '#f2994b', '#f2c84b', '#219654', '#2f82ed', '#9b51e0'];
    const [showQRCodeColorPicker, setShowQRCodeColorPicker] = useState(false);
    const cardPickerRef = useRef(null);
    const { cardId: paramCardId } = useParams();
    const { authState } = useAuth();
    const location = useLocation();

    // ✅ TEMPLATE CONTEXT: Use prop cardId if provided, otherwise use authenticated user's card if no cardId from params
    const isTemplateContext = !paramCardId || location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId || 
                      (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = propCardId || (isTemplateContext ? userCardId : paramCardId);

    const { qrCodeState, setQRCodeColor, initializeQRCode } = useQRCodeContext();
    const [qrCodeColor, setQrCodeColor] = useState('#000000'); // Local state for QR code color

    useEffect(() => {
        if (cardId) {
            initializeQRCode(cardId); // Ensure the QR code state is initialized for this card
        }
    }, [cardId, initializeQRCode]);

    // Set the local state `qrCodeColor` to match the context's current QR code color for the card
    useEffect(() => {
        if (cardId && qrCodeState[cardId]) {
            setQrCodeColor(qrCodeState[cardId]?.contextQrCodeColor || '#000000'); // Default to black if no color
        }
    }, [cardId, qrCodeState]);

    // Handle outside clicks to close the color picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cardPickerRef.current && !cardPickerRef.current.contains(event.target)) {
                setShowQRCodeColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleQRCodeColorPickerToggle = () => {
        setShowQRCodeColorPicker((prev) => !prev);
    };

    const handleQRCodeColorChangeComplete = (color) => {
        if (!cardId) {
            console.error('Card ID is required to update QR code color');
            return;
        }

        // Check if color change is restricted
        if (isColorRestricted) {
            if (onRestrictionNotification) {
                const message = canRequestChange
                    ? 'QR Code color is restricted by your subteam. You can submit a change request.'
                    : 'QR Code color is restricted by your subteam and cannot be changed.';
                onRestrictionNotification(message);
            }
            return;
        }

        setQrCodeColor(color.hex); // Update local state
        setQRCodeColor(cardId, color.hex); // Update context with the cardId
    };

    return (
        <Box
            sx={{
                marginBottom: 2,
                width: '60%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
            }}
        >
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '13px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Choose color
                {isColorRestricted && <LockOutlinedIcon sx={{ fontSize: '0.875rem', color: '#999' }} />}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '15px',
                }}
            >
                <IconButton
                    onClick={isColorRestricted ? undefined : handleQRCodeColorPickerToggle}
                    disabled={isColorRestricted}
                    sx={{
                        position: 'relative',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'transparent',
                        opacity: isColorRestricted ? 0.5 : 1,
                        cursor: isColorRestricted ? 'not-allowed' : 'pointer',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            border: `2px solid ${qrCodeColor}`,
                        }}
                    />
                    <ColorizeOutlinedIcon sx={{ color: '#828282', fontSize: 16 }} />
                </IconButton>

                {colorOptions.map((color, index) => (
                    <Box
                        key={index}
                        onClick={isColorRestricted ? undefined : () => handleQRCodeColorChangeComplete({ hex: color })}
                        sx={{
                            width: 24,
                            height: 24,
                            position: 'relative',
                            cursor: isColorRestricted ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isColorRestricted ? 0.5 : 1,
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                border: `2px solid ${qrCodeColor === color ? '#dbdbdb' : '#ffffff'}`,
                            }}
                        />
                        <Box
                            sx={{
                                width: '85%',
                                height: '85%',
                                borderRadius: '50%',
                                backgroundColor: color,
                            }}
                        />
                    </Box>
                ))}

                {showQRCodeColorPicker && (
                    <Box sx={{ position: 'absolute', top: '40px', right: 0, zIndex: 2 }} ref={cardPickerRef}>
                        <ChromePicker color={qrCodeColor} onChange={handleQRCodeColorChangeComplete} disableAlpha />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TeamQRCodeColorPicker;
